import { UserEvents, VideoEvents } from "./mixpanel.types";

import { logException } from "@punchup/sentry";

// Map analytics events to action names stored in the DB counter table
const ACTION_EVENT_MAP: Record<string, string> = {
  // [VideoEvents.VIDEO_SESSION_STARTED]: "watch",
  // [PaymentEvents.SUCCESSFUL_PAYMENT]: "purchase",
  [UserEvents.FOLLOWED_COMEDIAN]: "follow",
  [UserEvents.BUY_TICKETS_CLICKED]: "tickets",
  [VideoEvents.VIDEO_VIEWED_3S]: "videos",
  [UserEvents.TICKETS_COMPONENT_LOADED]: "visited-tickets-page",
};

// Track which events have been counted to prevent duplicates
const countedEvents = new Set<string>();

export const incrementSouthpawCounters = async ({
  event,
  shortCode,
  southpawJobId,
  devMode = false,
}: {
  event: string;
  shortCode?: string | undefined;
  southpawJobId?: string | undefined;
  devMode?: boolean;
}) => {
  let platform: "sms" | "email" | undefined;
  if (shortCode) {
    platform = "sms";
  }
  if (!shortCode && southpawJobId) {
    platform = "email";
  }

  // Create a unique key for this event + identifier combination
  const eventKey = `${event}_${shortCode || southpawJobId}`;

  // Only proceed if we have a valid platform and mapped event
  if (!platform || !ACTION_EVENT_MAP[event]) {
    return;
  }

  // Check if we've already counted this event
  if (countedEvents.has(eventKey)) {
    if (devMode) {
      console.log(
        "[incrementSouthpawCounters] 🔍 DEV MODE - Would skip (already counted this session)",
        { eventKey }
      );
    }
    return;
  }

  // Determine which RPC would be called
  let rpcName = "increment_link_click_count_from_email";
  if (event === UserEvents.TICKETS_COMPONENT_LOADED) {
    rpcName = "increment_link_click_count_from_email";
  } else if (event === UserEvents.FOLLOWED_COMEDIAN) {
    rpcName = "increment_link_follow_count";
  } else if (event === UserEvents.BUY_TICKETS_CLICKED) {
    rpcName = "increment_link_ticket_clicks";
  } else if (event === VideoEvents.VIDEO_VIEWED_3S) {
    rpcName = "increment_link_play_count";
  }

  // In dev mode, just log what WOULD happen
  if (devMode) {
    console.log(
      "[trackEvent] ⏭️  SKIPPING Mixpanel/Statsig & DB Counters - Dev mode",
      {
        platform,
        event,
        rpc: rpcName,
        jobId: southpawJobId,
        shortCode,
      }
    );
    countedEvents.add(eventKey); // Still track to prevent duplicate logs
    return;
  }

  // Mark as counted IMMEDIATELY to prevent race conditions
  countedEvents.add(eventKey);

  try {
    if (
      [
        UserEvents.FOLLOWED_COMEDIAN,
        UserEvents.BUY_TICKETS_CLICKED,
        VideoEvents.VIDEO_VIEWED_3S,
        UserEvents.TICKETS_COMPONENT_LOADED,
      ].includes(event as UserEvents | VideoEvents)
    ) {
      const response = await fetch("/api/v2/public/southpaw/count", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          event,
          sid: southpawJobId,
          platform,
          shortCode,
        }),
      });
      if (!response.ok) {
        throw new Error(
          `Failed to increment southpaw counter. Status: ${response.status}`
        );
      }
    }
  } catch (err) {
    // Remove from counted events if the API call failed so it can be retried
    countedEvents.delete(eventKey);
    logException(err, {
      tags: {
        log: "Failed to increment link action count",
      },
    });
  }
};
